/* global process */

import axios, { AxiosResponse } from "axios";
import { ExtensionsFilter } from "./model/ExtensionsFilter";
import { SearchResults } from "./model/SearchResults";
import { ClientHeader } from "./model/ClientHeader";
import { RunStatus } from "../helpers/asyncRun";

export const getClient = async (middletierToken: string, formData: FormData) =>
  axios({
    method: "POST",
    url: `${process.env.REACT_APP_BASE_URL_API}/client`,
    headers: {
      "X-Custom-Authorization": `Bearer ${middletierToken}`,
      "Content-Type": "multipart/form-data",
    },
    data: formData,
  });

export const searchClients = async (
  middletierToken: string,
  search: string,
  skip?: number
): Promise<AxiosResponse<SearchResults, any>> =>
  axios({
    method: "GET",
    // Encoded: #513 made this the first caller to pass an API-supplied client
    // name, and an ordinary one - "Meyer & Sohne GmbH" - would otherwise end
    // the query string early and hand the middle tier a truncated term.
    url: `${process.env.REACT_APP_BASE_URL_API}/search?term=${encodeURIComponent(search)}${
      skip ? `&skip=${skip}` : ""
    }`,
    headers: {
      "X-Custom-Authorization": `Bearer ${middletierToken}`,
    },
  });

/** POST /trigger: the mail, its attachments and the selected client, as multipart. */
export const postTrigger = async (middletierToken: string, formData: FormData) =>
  axios({
    method: "POST",
    url: `${process.env.REACT_APP_BASE_URL_API}/trigger`,
    headers: {
      "X-Custom-Authorization": `Bearer ${middletierToken}`,
      "Content-Type": "multipart/form-data",
    },
    data: formData,
  });

/**
 * An async run's status (#636). Without a runId, the user's latest run of the mail,
 * which is what a reopened pane asks for. Only the run's own user gets an answer;
 * anyone else, like a missing run, gets a 404.
 */
export const getTriggerStatus = async (
  middletierToken: string,
  mailID: string,
  runId?: string
): Promise<AxiosResponse<RunStatus>> =>
  axios({
    method: "GET",
    url: `${process.env.REACT_APP_BASE_URL_API}/trigger-status?mailID=${encodeURIComponent(mailID)}${
      runId ? `&runId=${encodeURIComponent(runId)}` : ""
    }`,
    // A poll that never settles would stop the wait, its give-up included: this one is
    // abandoned and asked again.
    timeout: 15000,
    headers: {
      "X-Custom-Authorization": `Bearer ${middletierToken}`,
    },
  });

/**
 * A Web PubSub client URL on which the user's runs' status changes are pushed (#716).
 * 503 where pushes are off: the wait then polls getTriggerStatus.
 */
export const getRunEvents = async (middletierToken: string): Promise<AxiosResponse<{ url: string }>> =>
  axios({
    method: "GET",
    url: `${process.env.REACT_APP_BASE_URL_API}/run-events`,
    timeout: 10000,
    headers: {
      "X-Custom-Authorization": `Bearer ${middletierToken}`,
    },
  });

export const getExtensions = async (middletierToken: string, filter: ExtensionsFilter) =>
  axios({
    method: "POST",
    url: `${process.env.REACT_APP_BASE_URL_API}/extensions`,
    headers: {
      "X-Custom-Authorization": `Bearer ${middletierToken}`,
      "Content-Type": "application/json",
    },
    data: filter,
  });

export const getClientHeaderData = async (
  middletierToken: string,
  topLevelPartnerID: number,
  topLevelPartnerType: number
): Promise<AxiosResponse<ClientHeader[]>> =>
  axios({
    method: "GET",
    url: `${process.env.REACT_APP_BASE_URL_API}/client-header?partnerid=${topLevelPartnerID}&partnertype=${topLevelPartnerType}`,
    headers: {
      "X-Custom-Authorization": `Bearer ${middletierToken}`,
    },
  });
